import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsDefaultMaterial,
  HsGraphicObject,
  HsGraphicPoints,
  HsPointsMaterial,
  HsShaderData,
  HsShaderObjectType,
  hsGenerateCubeGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  type HsDefaultShader,
  type HsGeometry,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
  type HsMaterial,
  type HsRegularGeometry,
  type HsShader,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import {
  makeCustomFragmentMaterial,
  setCustomFragment,
  setShaderTime,
} from '../../functions/custom-shader-material.function';
import { makeSdfPointCloudData } from '../../functions/sdf-point-cloud.function';

const CAMERA_POSITION: HsHypersceneTypes.Array3 = [2, 10, 30];
const POINTS_POSITION: HsHypersceneTypes.Array3 = [10, 4, 0];
const POINTS_SCALE: number = 3.5;
/** Points above this local height collide with the plane z = 0.3 of the legacy collision function. */
const POINTS_COLLISION_Z: number = 0.3;

const INVERTED_SHADER_CODE: string = `
void main() {
  gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;
  fragmentColor = vec4(vec3(1.0) - v_color, 1.0);
}
`;

const FLOOR_SHADER_CODE: string = `
mat2 rot(float a) {
  float s = sin(a);
  float c = cos(a);
  return mat2(c, -s, s, c);
}

void main() {
  gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;
  vec2 pos = v_position.xz * 2.2;
  pos.x -= 2.0;
  pos *= rot(3.1415 * 0.25);
  float threshold = sin(pos.x) + sin(pos.y);
  float edgeMin = -0.01;
  float edgeMax = 0.01;
  float effect = clamp(fract(u_time / 4.0) * 4.0 - 2.0, 0.0, 1.0);
  edgeMax -= abs(effect - 0.5) - 0.5;
  float cells = smoothstep(edgeMax, edgeMin, threshold);
  vec3 color = vec3(cells * 0.8 + 0.2);
  fragmentColor = vec4(color + v_color * 0.1, 1.0);
}
`;

// v_position is the negated world position.
const COLORS_SHADER_CODE: string = `
void main() {
  gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;
  vec3 color = vec3(sin(-v_position.xyz) * 0.5 + 0.5);
  fragmentColor = vec4(v_color * color, 1.0);
}
`;

// Ray marches a torus from the cube surface along the view ray; v_point is the camera-relative position.
const RAY_SHADER_CODE: string = `
float getDist(vec3 p) {
  p += vec3(5.0, -2.5, 0.0);
  vec2 q = vec2(length(p.xz) - 2.0, p.y);
  return length(q) - 0.8;
}

vec3 getNormal(vec3 p) {
  float d = getDist(p);
  vec2 e = vec2(0.0001, 0.0);
  vec3 n = d - vec3(getDist(p - e.xyy), getDist(p - e.yxy), getDist(p - e.yyx));
  return normalize(n);
}

float march(vec3 ro, vec3 rd) {
  vec3 light = normalize(vec3(-0.5, 1.0, 0.5));
  vec3 p = ro;
  for (int i = 0; i < 200; i++) {
    float d = getDist(p);
    if (d > 20.0) discard;
    if (d < 0.005) {
      vec3 n = getNormal(p);
      vec3 refDir = reflect(rd, n);
      float dif = dot(n, light) * 0.5 + 0.6;
      float ref = dot(light, refDir) * 0.5 + 0.5;
      ref = pow(ref, 256.0);
      return dif + ref;
    }
    p += rd * d;
  }
  discard;
}

void main() {
  gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;
  vec3 direction = normalize(v_point.xyz);
  vec3 color = vec3(march(-v_position.xyz, direction)) * vec3(1.0, 0.5, 0.8);
  fragmentColor = vec4(color, 1.0);
}
`;

/** World height above which the point cloud collides; the legacy engine passed a per-point collision flag. */
function makePointsShaderCode(collisionWorldZ: number): string {
  return `
void main() {
  gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;
  vec2 uv = gl_PointCoord * 2.0 - 1.0;
  if (length(uv) > 1.0) {
    discard;
  }
  float collision = step(${collisionWorldZ.toFixed(4)}, -v_position.z);
  vec3 collisionColor = vec3(1.0, sin(u_time * 3.0) * 0.5 + 0.5, 0.0);
  vec3 color = mix(v_color, collisionColor, collision * 0.5);
  fragmentColor = vec4(color, 1.0);
}
`;
}

/** Meshes and a point cloud drawn with custom fragment shaders; the floor and the points animate over time. */
@Component({
  selector: 'hyperscene-custom-shaders',
  templateUrl: './custom-shaders.component.html',
  styleUrls: ['./custom-shaders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class CustomShadersDemo extends DemoViewerPage {
  private readonly animatedShaders: HsShader[] = [];
  private readonly startedAt: number = performance.now();

  protected initScene(viewer: HsHypersceneViewer): void {
    const gl: WebGL2RenderingContext = viewer.gl;
    const cubeGeometry: HsRegularGeometry | null = hsMakeGeometry(
      viewer.surface.graphics,
      hsGenerateCubeGeometryData()
    );
    const indexedCubeGeometry: HsGeometry | null = hsMakeIndexedGeometry(
      viewer.surface.graphics,
      hsIndexGeometry(hsGenerateCubeGeometryData())
    );
    const pointsGeometry: HsRegularGeometry | null = hsMakeGeometry(
      viewer.surface.graphics,
      makeSdfPointCloudData(20, (x: number, y: number, z: number) => x ** 2 + y ** 2 + z ** 2 - 1)
    );
    if (cubeGeometry === null || indexedCubeGeometry === null || pointsGeometry === null) {
      return;
    }

    const litShaderData = (): HsShaderData =>
      new HsShaderData(HsShaderObjectType.single).addColor().addDepth().addNormals().addDiffuseLighting();

    const invertedMaterial: HsDefaultMaterial<HsDefaultShader> = makeCustomFragmentMaterial(
      gl,
      litShaderData(),
      INVERTED_SHADER_CODE
    );
    invertedMaterial.color.set(1, 1, 1);
    // Shares the compiled inverted shader with another color.
    const invertedOrangeMaterial: HsMaterial = new HsDefaultMaterial(gl, invertedMaterial.getShader(), false);
    invertedOrangeMaterial.color.set(1, 0.75, 0);

    const colorsMaterial: HsMaterial = makeCustomFragmentMaterial(gl, litShaderData(), COLORS_SHADER_CODE);
    colorsMaterial.color.set(1, 1, 1);

    const rayMaterial: HsMaterial = makeCustomFragmentMaterial(
      gl,
      new HsShaderData(HsShaderObjectType.single).addColor().addDepth().addRayIntersection(),
      RAY_SHADER_CODE,
      'in vec4 v_point;'
    );

    const floorMaterial: HsMaterial = makeCustomFragmentMaterial(gl, litShaderData(), FLOOR_SHADER_CODE);
    floorMaterial.color.set(1, 1, 1);

    const collisionWorldZ: number = POINTS_POSITION[2] + POINTS_COLLISION_Z * POINTS_SCALE;
    const pointsShader: HsDefaultShader = setCustomFragment(
      new HsShaderData(HsShaderObjectType.single).addColor().addDepth().addPoints().makeShader(),
      makePointsShaderCode(collisionWorldZ)
    );
    const pointsMaterial: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial(gl, pointsShader);

    this.animatedShaders.push(floorMaterial.getShader(), pointsShader);

    this.addMesh(viewer, cubeGeometry, invertedMaterial, [-10, 2, 0], [2, 2, 2]);
    this.addMesh(viewer, indexedCubeGeometry, invertedOrangeMaterial, [-15, 2, 0], [2, 2, 2]);
    this.addMesh(viewer, indexedCubeGeometry, colorsMaterial, [0, 2, 0], [2, 2, 2]);
    this.addMesh(viewer, indexedCubeGeometry, rayMaterial, [-5, 2, 0], [3, 3, 3]);
    this.addMesh(viewer, cubeGeometry, floorMaterial, [0, -2, 0], [40, 1, 40]);

    const points: HsGraphicPoints = new HsGraphicPoints(getUuid(), 'points', pointsGeometry, pointsMaterial);
    points.setPointSize(25);
    points.worldMatrix.setPosition(POINTS_POSITION).scale([POINTS_SCALE, POINTS_SCALE, POINTS_SCALE]);
    points.updateBoundingSphere();
    viewer.add(points);

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition(CAMERA_POSITION);
    viewer.camera.updateProjectionMatrix();
  }

  protected override animate(viewer: HsHypersceneViewer): boolean {
    setShaderTime(viewer.gl, this.animatedShaders, (performance.now() - this.startedAt) / 1000);
    return false;
  }

  private addMesh(
    viewer: HsHypersceneViewer,
    geometry: HsGeometry,
    material: HsMaterial,
    position: HsHypersceneTypes.Array3,
    scale: HsHypersceneTypes.Array3
  ): void {
    const mesh: HsGraphicObject = new HsGraphicObject(getUuid(), 'mesh', geometry, material);
    mesh.worldMatrix.setPosition(position).scale(scale);
    mesh.updateBoundingSphere();
    viewer.add(mesh);
  }
}
